import { JSX, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { CompetitiveInsightsManageAddModal } from "@app/overviews/network-benchmarking-dashboard/manage/CompetitiveInsightsManageAddModal";
import { CompetitiveInsightsManageContext, getLineOfBusinessDisplayName } from "@app/overviews/network-benchmarking-dashboard/manage/CompetitiveInsightsManageContext";
import { CompetitiveInsightsSupersetProjectConfigurations } from "@app/overviews/network-benchmarking-dashboard/manage/shared/CompetitiveInsightsSupersetProjectConfigurations";
import { timeDistanceCalculatorTypeDisplayMap } from "@app/projects/timeDistanceCalculatorTypeDisplayMap";
import { Badge } from "@app/shared/Badge";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QesColors } from "@app/shared/qes-standards/colors";

// listed in order of appearance
interface CompetitiveInsightsManageTableToppings {
  project: JSX.Element;
  lineOfBusiness: string;
  state: string;
  client: string;
  program: string;
  template: JSX.Element;
  providerSet: string;
  specialtyCrosswalk: string;
  membershipSet: string;
  status: JSX.Element;
  timeDistanceCalculatorType: string;
  internalPreviewOnly: string;
  actions: JSX.Element;
  [wafflesKeyColumnName]: string;
}

const activeDotIconStyle = { fill: "currentColor", fontSize: "8px", verticalAlign: "top" };

const getColumnConfig = (isQuestInternal: boolean): ColumnConfig => {
  return {
    /* oxlint-disable sort-keys */
    project: { displayText: "Project", sticky: true, testId: "project" },
    lineOfBusiness: { displayText: "Line of Business", testId: "line-of-business" },
    state: { displayText: "State", testId: "state" },
    client: { displayText: "Client", hidden: !isQuestInternal, testId: "client" },
    program: { displayText: "Program", testId: "program" },
    template: { displayText: "Template", testId: "template" },
    providerSet: { displayText: "Provider Set", testId: "provider-set" },
    specialtyCrosswalk: { displayText: "Specialty Crosswalk", testId: "specialty-crosswalk" },
    membershipSet: { displayText: "Membership Set", testId: "membership-set" },
    status: { displayText: "Status", sizeContentToFit: true, testId: "status" },
    timeDistanceCalculatorType: { displayText: "Template Time and Distance", testId: "time-distance-calculator-type" },
    internalPreviewOnly: { displayText: "Internal Preview Only", testId: "internal-preview-only" },
    actions: { displayText: "Actions", minWidth: "90px", noSort: true, sizeContentToFit: true, testId: "actions" },
    /* oxlint-enable sort-keys */
  };
};

const buildCompetitiveInsightsManageTableWaffleData = (
  projects: CompetitiveInsightsSupersetProjectConfigurations[]
): CompetitiveInsightsManageTableToppings[] => {
  return projects.map((project) => {
    /* oxlint-disable sort-keys */
    return {
      project: !!project.projectName ? (
        <Link to={`/projects/${project.projectId}`} target="_blank">{project.projectName}</Link>
      ) : (
        <>---</>
      ),
      lineOfBusiness: getLineOfBusinessDisplayName(project.lineOfBusiness) || "---",
      state: project.stateAbbreviation || "---",
      client: project.clientName || "---",
      program: project.program || "---",
      template: !!project.templateCollectionName ? (
        <>
          <div>{project.templateCollectionName}</div>
          <div className="text-muted">{`ID: ${project.templateId || "---"}`}</div>
        </>
      ) : (
        <>---</>
      ),
      providerSet: project.providerSetName || "---",
      specialtyCrosswalk: project.specialtyCodeMapSetName || "---",
      membershipSet: project.membershipSetName || "---",
      timeDistanceCalculatorType: timeDistanceCalculatorTypeDisplayMap[project.timeDistanceCalculatorType] || "---",
      internalPreviewOnly: project.internalUserOnly == null ? "---" : (project.internalUserOnly ? "Yes" : "No"),
      status: project.isActive == null ? (
        <>---</>
      ) : project.isActive ? (
        <Badge badgeType="badge--success">
          <QesIcon iconName={QesIcons.Circle} isDecorative style={activeDotIconStyle} /> Active
        </Badge>
      ) : (
        <Badge badgeType="badge--neutral">
          <QesIcon iconName={QesIcons.Circle} isDecorative style={activeDotIconStyle} /> Inactive
        </Badge>
      ),
      actions: (
        <>
          <CompetitiveInsightsManageAddModal configuration={project} mode="edit" />
          <QESButton
            disabled={true}
            qesType="secondary"
            className="p-1"
            qesTestId={`competitive-insights__manage--table-delete-${project.projectId}`}
            aria-label="Delete"
          >
            <QesIcon iconName={QesIcons.TrashV1} color={QesColors.QesPrimaryRed} description="Delete" />
          </QESButton>
        </>
      ),
      [wafflesKeyColumnName]: project.projectId.toString(),
    };
    /* oxlint-enable sort-keys */
  });
};

const CompetitiveInsightsManageTable = (): JSX.Element => {
  const competitiveInsightsManageContext = useContext(CompetitiveInsightsManageContext);

  const projectsById = useMemo(
    () => new Map(competitiveInsightsManageContext.filteredProjects.map(x => [x.projectId.toString(), x])),
    [competitiveInsightsManageContext.filteredProjects]
  );

  const getProject = (key: string): CompetitiveInsightsSupersetProjectConfigurations => {
    return projectsById.get(key);
  }

  const handleSort = (
    segment: CompetitiveInsightsManageTableToppings[],
    column: keyof CompetitiveInsightsManageTableToppings,
    direction: SortDirections
  ): CompetitiveInsightsManageTableToppings[] => {
    if (direction === SortDirections.None) {
      return segment;
    }

    const multiplier = direction === SortDirections.Asc ? 1 : -1;

    switch (column) {
      case "lineOfBusiness":
      case "state":
      case "client":
      case "program":
      case "providerSet":
      case "specialtyCrosswalk":
      case "membershipSet":
      case "timeDistanceCalculatorType":
      case "internalPreviewOnly":
        return [...segment].sort((a, b) => a[column].localeCompare(b[column]) * multiplier);
      case "template":
        return [...segment].sort((a, b) => {
          const projectA = getProject(a[wafflesKeyColumnName]);
          const projectB = getProject(b[wafflesKeyColumnName]);
          const nameComparison = (projectA.templateCollectionName || "").localeCompare(projectB.templateCollectionName || "");
          return (nameComparison !== 0 ? nameComparison : (projectA.templateId || "").localeCompare(projectB.templateId || "")) * multiplier;
        });
      case "status": {
        const statusSortValue = (key: string): string => getProject(key).isActive ? "Active" : "Inactive";
        return [...segment].sort((a, b) => statusSortValue(a[wafflesKeyColumnName]).localeCompare(statusSortValue(b[wafflesKeyColumnName])) * multiplier);
      }
      case "project":
        return [...segment].sort((a, b) => (getProject(a[wafflesKeyColumnName]).projectName || "").localeCompare(getProject(b[wafflesKeyColumnName]).projectName || "") * multiplier);
      default:
        return segment;
    }
  };

  const waffleData = useMemo(
    () => buildCompetitiveInsightsManageTableWaffleData(competitiveInsightsManageContext.filteredProjects),
    [competitiveInsightsManageContext.filteredProjects]
  );
  const columnConfig = useMemo(() => getColumnConfig(competitiveInsightsManageContext.isQuestInternal), [competitiveInsightsManageContext.isQuestInternal]);

  return (
    <Waffles<CompetitiveInsightsManageTableToppings>
      columns={columnConfig}
      customSort={handleSort}
      data={waffleData}
      fullWidth={true}
      loading={competitiveInsightsManageContext.projectsLoading}
      testId="competitive-insights__manage--table"
    />
  );
};

export default CompetitiveInsightsManageTable;
